import '@eneco/common/application-insights';

import { FC, ReactNode, useEffect, useState } from 'react';

import { ApplicationProvider, I18n } from '@eneco/common/application';
import { ErrorBoundary } from '@eneco/common/log';
import { removeHostedBasePath } from '@eneco/common/path';
import { SWRCacheProvider } from '@eneco/common/swr';
import { UTMCapture } from '@eneco/components/UTMCapture';
import { Label } from '@eneco/dc/client/types';
import { DigitalCoreProvider } from '@eneco/dc/provider';
import { LinkComponentProvider } from '@eneco/link';
import { Head, Link, useRouter } from '@eneco/next';
import { NbaDataProvider, type NbaDataType } from '@eneco/next-best-action';
import { OverloadGuard } from '@eneco/overload';
import { SparkyProvider } from '@eneco/sparky/providers';
import { GTMProvider, PageViewTracker, TrackingProvider } from '@eneco/tracking';
import type { PageViewProfile } from '@eneco/tracking';
import type { DC_Repositories_Base_Enumerations_BusinessUnit } from '@monorepo-types/dc';
import { fetchPlaceholder } from '@sitecore/client/browser';
import { ComponentsDefinition, PlaceholderProvider, SitecoreContext, VisitorIdentification } from '@sitecore/common';
import type { LayoutServiceData, LayoutServiceExperimentInfo } from '@sitecore/types/lib';
import { Session } from 'next-auth';

import { ErrorPageContainer } from './Error';
import { MockConfigProvider } from './mock-config-provider';
import { SitecoreCmsProvider } from './SitecoreCmsProvider';

export type SitecoreRouteProps = {
  appBannerAppId?: string;
  appBannerAppArgument?: string;
  appBannerPaths?: string[];
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  children: ReactNode;
  /** SHA-256 of the customer id, hashed server-side, for the page_view event. */
  clientIdEncoded?: string | null;
  components: ComponentsDefinition;
  dataLayerEvents?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  i18n: I18n;
  idToken?: Session['idToken'] | null;
  label: Label;
  layoutData: LayoutServiceData;
  name: string;
  nbaData?: NbaDataType;
  /** Account signals for the page_view event; null when anonymous or non-NL. */
  pageViewProfile?: PageViewProfile | null;
  path: string;
  platformHint?: 'desktop' | 'mobile';
  user?: Session['user'] | null;
};

const SitecoreRouteContainer: FC<SitecoreRouteProps> = ({
  appBannerAppId,
  appBannerAppArgument,
  appBannerPaths,
  businessUnit,
  children,
  clientIdEncoded,
  components,
  dataLayerEvents,
  i18n,
  idToken,
  label,
  layoutData,
  name,
  nbaData,
  pageViewProfile,
  path,
  platformHint,
  user,
}) => {
  const route = layoutData?.sitecore?.route;
  const browserTitleValue = route?.fields?.browserTitle?.value;
  const pageTitleValue = route?.fields?.pageTitle?.value;

  // Ensure we only use string values for the title
  const title =
    (typeof browserTitleValue === 'string' ? browserTitleValue : null) ||
    (typeof pageTitleValue === 'string' ? pageTitleValue : null) ||
    label;
  const theme = route?.fields?.sparkyThemeOption?.value?.toString() ?? 'default';
  const metaDescription = route?.fields?.metaDescription?.value;
  const metaKeywords = route?.fields?.metaKeywords?.value;
  const [experimentInfo, setExperimentInfo] = useState<LayoutServiceExperimentInfo | undefined>(undefined);
  const sc_experimentInfo = layoutData?.sitecore?.context?.experimentInfo;

  const { asPath, activePath } = useRouter();

  function getIndexFollowStatus(): string {
    const { doNotFollow, doNotIndex } = route?.fields ?? {};
    const followStatus = doNotFollow?.value ? 'nofollow' : 'follow';
    const indexStatus = doNotIndex?.value ? 'noindex' : 'index';
    return `${indexStatus}, ${followStatus}`;
  }

  // Make the experimentInfo available on the global object for debugging,
  // and cache it so we always have the last defined version.
  useEffect(() => {
    if (sc_experimentInfo) {
      setExperimentInfo(sc_experimentInfo);
    }
    (window as any).experimentInfo = sc_experimentInfo || experimentInfo; // eslint-disable-line @typescript-eslint/no-explicit-any
  }, [sc_experimentInfo, experimentInfo]);

  const showAppBanner =
    !!appBannerAppId &&
    !!appBannerAppArgument &&
    !!appBannerPaths?.length &&
    appBannerPaths.some(p => removeHostedBasePath(activePath).startsWith(p));

  return (
    <ErrorBoundary
      fallback={
        <ErrorPageContainer
          businessUnit={businessUnit}
          components={components}
          i18n={i18n}
          label={label}
          layoutData={null}
          name={name}>
          {children}
        </ErrorPageContainer>
      }>
      <Head>
        <title>{title}</title>
        {<meta name="robots" content={getIndexFollowStatus()} />}
        {metaDescription && <meta name="description" content={String(metaDescription)} />}
        {metaKeywords && <meta name="keywords" content={String(metaKeywords)} />}
        {showAppBanner && (
          <meta
            name="apple-itunes-app"
            content={`app-id=${appBannerAppId}, ct=AppSmartBanner, pt=1473003, app-argument=${appBannerAppArgument}`}
          />
        )}
      </Head>
      <MockConfigProvider>
        <ApplicationProvider i18n={i18n} path={path} isEditMode={!!layoutData?.sitecore?.context?.pageEditing}>
          <NbaDataProvider data={nbaData}>
            <GTMProvider
              eventsFromSSR={dataLayerEvents}
              experimentInfo={experimentInfo}
              customerId={layoutData.sitecore.context?.customerId ?? user?.customerId}
              accountId={layoutData.sitecore.context?.accountId}
              businessUnit={businessUnit}
              label={label}
              useLegacy={businessUnit !== 'nl'}>
              <TrackingProvider scope={name}>
                <SparkyProvider locale={i18n.locale} platformHint={platformHint} theme={theme}>
                  <SitecoreContext components={components} layoutData={layoutData}>
                    <PlaceholderProvider
                      fetchPlaceholder={fetchPlaceholder}
                      idToken={idToken}
                      currentRoutePath={asPath}>
                      <UTMCapture />
                      <VisitorIdentification defer />
                      <SWRCacheProvider>
                        <DigitalCoreProvider
                          user={user}
                          idToken={idToken}
                          customerContext={layoutData.sitecore.context}
                          label={label}
                          businessUnit={businessUnit}>
                          {/*
                            Mounted here, not next to GTMProvider: page_view needs
                            useDC() (login_status), useMediaQuery() (viewport_type)
                            and layoutData (page_type), and GTMProvider sits above
                            all three.
                          */}
                          <PageViewTracker
                            clientId={layoutData.sitecore.context?.customerId ?? user?.customerId}
                            clientIdEncoded={clientIdEncoded}
                            enabled={businessUnit === 'nl'}
                            // TODO: page_release needs the Sitecore item's __Updated
                            // date on the layout context; undefined until that lands.
                            pageType={layoutData?.sitecore?.route?.templateName}
                            profile={pageViewProfile}>
                            <OverloadGuard>
                              <LinkComponentProvider component={Link}>
                                <SitecoreCmsProvider layoutData={layoutData} components={components}>
                                  {children}
                                </SitecoreCmsProvider>
                              </LinkComponentProvider>
                            </OverloadGuard>
                          </PageViewTracker>
                        </DigitalCoreProvider>
                      </SWRCacheProvider>
                    </PlaceholderProvider>
                  </SitecoreContext>
                </SparkyProvider>
              </TrackingProvider>
            </GTMProvider>
          </NbaDataProvider>
        </ApplicationProvider>
      </MockConfigProvider>
    </ErrorBoundary>
  );
};

export { SitecoreRouteContainer };
